// ============================================================
// MANIC MEETER — Deutsche Inhalte: Rollen, Abteilungen, Meetings
// ============================================================

import { ColleagueRole, Department, MeetingType, PlayerRole } from "../../types";
import type { MeetingTypeContent } from "../contract";

export const DEPARTMENTS: Record<Department, string> = {
  [Department.Frontend]: "Frontend",
  [Department.Backend]: "Backend",
  [Department.Client]: "Client",
  [Department.HR]: "HR",
  [Department.Marketing]: "Marketing",
  [Department.Management]: "Management",
};

export const COLLEAGUE_ROLES: Record<ColleagueRole, string> = {
  [ColleagueRole.FrontendDev]: "Frontend-Entwickler",
  [ColleagueRole.BackendDev]: "Backend-Entwickler",
  [ColleagueRole.ClientManager]: "Client Manager",
  [ColleagueRole.HRManager]: "HR-Manager",
  [ColleagueRole.MarketingGuru]: "Marketing-Guru",
  [ColleagueRole.Chef]: "Chef",
  [ColleagueRole.Abteilungsleiter]: "Abteilungsleiter",
  [ColleagueRole.Teamleiter]: "Teamleiter",
  [ColleagueRole.Designer]: "UX-Designer",
  [ColleagueRole.Praktikant]: "Praktikant",
  [ColleagueRole.ScrumMaster]: "Scrum Master",
  [ColleagueRole.ProductOwner]: "Product Owner",
  [ColleagueRole.Sprachbarriere]: "London-Office",
};

export const PLAYER_ROLES: Record<PlayerRole, string> = {
  [PlayerRole.Praktikant]: "Praktikant",
  [PlayerRole.Azubi]: "Azubi",
  [PlayerRole.Werkstudent]: "Werkstudent",
  [PlayerRole.JuniorDev]: "Junior-Dev",
};

export const MEETING_TYPES: Record<MeetingType, MeetingTypeContent> = {
  [MeetingType.DailyStandup]: {
    name: "Daily Standup",
    description: "15 Minuten, die sich wie 2 Stunden anfühlen.",
  },
  [MeetingType.Planning]: {
    name: "Sprint Planning",
    description: "Sprint Planning: Wo Schätzungen geboren werden, um zu sterben.",
  },
  [MeetingType.LunchMeeting]: {
    name: "Lunch-Meeting",
    description: "Dein Mittagessen wird kalt, während jemand über KPIs redet.",
  },
  [MeetingType.OneOnOne]: {
    name: "1-on-1",
    description: "Nur du und dein Teamleiter. Kein Entkommen.",
  },
  [MeetingType.Scrum]: {
    name: "Scrum-Meeting",
    description: "Agile™ in Reinform. Fühlt sich trotzdem wie Wasserfall an.",
  },
  [MeetingType.Schulung]: {
    name: "Schulung",
    description: "Pflichtschulung: 'Compliance im digitalen Zeitalter'.",
  },
  [MeetingType.Retro]: {
    name: "Retrospektive",
    description: "Was lief gut? Was lief schlecht? (Alles.)",
  },
  [MeetingType.AllHands]: {
    name: "All-Hands",
    description: "Der Chef redet. Alle nicken. Niemand hört zu.",
  },
  [MeetingType.Brainstorming]: {
    name: "Brainstorming-Session",
    description: "Post-Its, Whiteboards und null Ergebnisse.",
  },
  [MeetingType.StatusUpdate]: {
    name: "Status Update",
    description: "Alles was in einer E-Mail hätte stehen können.",
  },
  [MeetingType.Vorstandsmeeting]: {
    name: "Vorstandsmeeting",
    description: "Der Vorstand hat Fragen. Viele Fragen. Und du hast keine Antworten.",
  },
};

// ---- ASCII-Szenen über dem Meeting ----

export const MEETING_SCENES: Record<MeetingType, string> = {
  [MeetingType.DailyStandup]: `<pre class="ascii-scene">
+-------------------+
| DAILY STANDUP  .. |
|  +-----------+    |
|  | Kanban    |    |
|  | ####::::: |    |
|  +-----------+    |
+-------------------+</pre>`,
  [MeetingType.Planning]: `<pre class="ascii-scene">
+-------------------+
|  SPRINT PLANNING  |
|  +--+--+--+--+    |
|  |S1|S2|S3|S4|    |
|  +--+--+--+--+    |
|  |13| 8| 5|21|    |
|  +--+--+--+--+    |
|  Story Points     |
+-------------------+</pre>`,
  [MeetingType.LunchMeeting]: `<pre class="ascii-scene">
+-------------------+
|  LUNCH MEETING    |
|  +-----------+    |
|  | ~~~~~~~~~ |    |
|  |  Kantine  |    |
|  +-----------+    |
+-------------------+</pre>`,
  [MeetingType.OneOnOne]: `<pre class="ascii-scene">
+-------------------+
|     1-ON-1        |
|                   |
|   o      o        |
|  /|\    /|\       |
|  / \    / \       |
|  [__]   [__]      |
+-------------------+</pre>`,
  [MeetingType.Scrum]: `<pre class="ascii-scene">
+-------------------+
|  SCRUM MEETING    |
|  +===========+    |
|  | Sprint ## |    |
|  | Velocity^ |    |
|  +===========+    |
|  ~ Iteration ~    |
+-------------------+</pre>`,
  [MeetingType.Schulung]: `<pre class="ascii-scene">
+-------------------+
|  SCHULUNG         |
|  +============+   |
|  | Compliance |   |
|  | ########## |   |
|  | Folie 3/47 |   |
|  +============+   |
+-------------------+</pre>`,
  [MeetingType.Retro]: `<pre class="ascii-scene">
+-------------------+
|  RETROSPEKTIVE    |
|  +--+ +--+ +--+   |
|  |:)| |:-| |:(|   |
|  +--+ +--+ +--+   |
|  Post-Its         |
|  =============    |
+-------------------+</pre>`,
  [MeetingType.AllHands]: `<pre class="ascii-scene">
+-------------------+
|   ALL-HANDS       |
|   +==========+    |
|   | ##  /\   |    |
|   | ## /  \  |    |
|   +==========+    |
|  ooooooo ooooooo  |
+-------------------+</pre>`,
  [MeetingType.Brainstorming]: `<pre class="ascii-scene">
+-------------------+
|  BRAINSTORMING *  |
|  +------------+   |
|  | ## ## ## ## |   |
|  | Whiteboard  |   |
|  | ## ## ## ## |   |
|  +------------+   |
+-------------------+</pre>`,
  [MeetingType.StatusUpdate]: `<pre class="ascii-scene">
+-------------------+
|  STATUS UPDATE    |
|  > --------       |
|  > --------       |
|  > --------       |
|  +===========+    |
|  | [x][x][ ] |    |
|  +===========+    |
+-------------------+</pre>`,
  [MeetingType.Vorstandsmeeting]: `<pre class="ascii-scene ascii-scene-boss">
+===================+
|  VORSTANDSMEETING |
|  ===============  |
|  +-------------+  |
|  | Q3 ZAHLEN   |  |
|  | STRATEGIE ^ |  |
|  +-------------+  |
|  !! VORSICHT !!   |
+===================+</pre>`,
};
